import React, { useRef, useState, useCallback, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { useTheme } from '../../hooks/useTheme';
import {
  parseBpmnDsl,
  generateBpmnXmlFromDsl,
  isBpmnDsl,
  DEFAULT_BPMN_DSL,
  buildElementMap,
} from '../../utils/bpmnDslParser';
import {
  setupBpmnDslLanguage,
  updateBpmnRegistryEntities,
  computeBpmnDiagnostics,
} from '../../utils/monacoSetupBpmn';
import { getEntitiesMap } from '../../api/systemsApi';

const PANEL_WIDTH = 640;
const HEADER_H = 32;

/**
 * BPMN DSL editor panel.
 *
 * Props:
 *   onClose           () => void
 *   onGenerateBpmn    (xml: string, elementMap?) => void
 *   dslText           string   — DSL content pushed from parent
 *   onElementMapChange (map) => void
 *   onDslChange       (dsl: string) => void
 */
const BpmnCodePanel = ({ onClose, onGenerateBpmn, dslText, onElementMapChange, onDslChange }) => {
  const { theme } = useTheme();
  const initialDsl = dslText !== undefined ? dslText : DEFAULT_BPMN_DSL;
  const dslValueRef = useRef(initialDsl);
  const [generateError, setGenerateError] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const [editorDefault, setEditorDefault] = useState(initialDsl);
  const monacoRef = useRef(null);
  const editorRef = useRef(null);
  const programmaticValueChangeRef = useRef(false);
  const [warnings, setWarnings] = useState([]);

  const setEditorValueProgrammatically = useCallback((value) => {
    if (!editorRef.current) return;
    programmaticValueChangeRef.current = true;
    try {
      editorRef.current.setValue(value);
    } finally {
      programmaticValueChangeRef.current = false;
    }
  }, []);

  // ── Load systems registry on mount ──
  useEffect(() => {
    getEntitiesMap()
      .then((map) => {
        updateBpmnRegistryEntities(map);
      })
      .catch((err) => console.warn('BPMN registry load failed:', err));
  }, []);

  // ── Monaco editor mount handler ──
  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    setupBpmnDslLanguage(monaco);

    if (dslText !== undefined) {
      setEditorValueProgrammatically(dslValueRef.current);
    }

    const initialMarkers = computeBpmnDiagnostics(dslValueRef.current, monaco);
    monaco.editor.setModelMarkers(editor.getModel(), 'bpmn-dsl', initialMarkers);
    setWarnings(initialMarkers.filter(m => m.code === 'UNLINKED_LANE'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dslText, setEditorValueProgrammatically]);

  const applyDiagnostics = useCallback((value) => {
    if (!monacoRef.current || !editorRef.current) return;
    if (!isBpmnDsl(value)) {
      setWarnings([]);
      monacoRef.current.editor.setModelMarkers(editorRef.current.getModel(), 'bpmn-dsl', []);
      return;
    }
    const markers = computeBpmnDiagnostics(value, monacoRef.current);
    monacoRef.current.editor.setModelMarkers(editorRef.current.getModel(), 'bpmn-dsl', markers);
    setWarnings(markers.filter(m => m.code === 'UNLINKED_LANE'));
  }, []);

  // When parent pushes new DSL text (e.g. after Sync to Code or load), update the editor
  useEffect(() => {
    if (dslText === undefined || !editorRef.current) return;
    setEditorValueProgrammatically(dslText);
    dslValueRef.current = dslText;
    setGenerateError('');
    applyDiagnostics(dslText);
  }, [dslText, applyDiagnostics, setEditorValueProgrammatically]);

  const monacoTheme = theme.name === 'dark' ? 'bpmn-dsl-dark' : 'bpmn-dsl-light';
  const borderColor = theme.toolbar?.border || '#e2e8f0';
  const bg = theme.toolbar?.background || '#fff';
  const textColor = theme.toolbar?.text || '#1e293b';
  const accentColor = '#3b82f6';

  const handleDslChange = useCallback((value) => {
    dslValueRef.current = value || '';
    if (programmaticValueChangeRef.current) return;
    onDslChange?.(dslValueRef.current);
    if (isBpmnDsl(dslValueRef.current)) {
      const ast = parseBpmnDsl(dslValueRef.current);
      if (ast) onElementMapChange?.(buildElementMap(ast));
    }
    setGenerateError('');
    applyDiagnostics(dslValueRef.current);
  }, [applyDiagnostics, onElementMapChange, onDslChange]);

  const handleGenerateBpmn = useCallback(() => {
    const ast = parseBpmnDsl(dslValueRef.current);
    if (!ast) {
      setGenerateError('Parse error: check diagram header syntax.');
      return;
    }
    const xml = generateBpmnXmlFromDsl(ast);
    if (!xml) {
      setGenerateError('No elements defined in DSL.');
      return;
    }
    const elementMap = buildElementMap(ast);
    setGenerateError('');
    onGenerateBpmn?.(xml, elementMap);
  }, [onGenerateBpmn]);

  const sharedEditorOptions = {
    fontSize: 13,
    minimap: { enabled: false },
    lineNumbers: 'on',
    scrollBeyondLastLine: false,
    wordWrap: 'off',
    automaticLayout: true,
    quickSuggestions: { other: true, comments: false, strings: true },
    quickSuggestionsDelay: 0,
    suggestOnTriggerCharacters: true,
    acceptSuggestionOnEnter: 'off',
    acceptSuggestionOnCommitCharacter: false,
    tabCompletion: 'on',
    suggest: {
      snippetsPreventQuickSuggestions: false,
      showWords: false,
    },
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: 56,
        right: 0,
        bottom: 0,
        width: PANEL_WIDTH,
        display: 'flex',
        flexDirection: 'column',
        background: bg,
        borderLeft: `1px solid ${borderColor}`,
        zIndex: 1500,
      }}
    >
      {/* ── DSL pane ── */}
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <PaneHeader
          title="BPMN DSL"
          bg={bg}
          textColor={textColor}
          borderColor={borderColor}
          right={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                onClick={handleGenerateBpmn}
                title="Generate BPMN diagram from DSL"
                style={generateButtonStyle(accentColor)}
              >
                ▶ Generate BPMN
              </button>
              <button onClick={onClose} title="Close" style={closeButtonStyle(textColor)}>
                ✕
              </button>
            </div>
          }
        />

        {generateError && (
          <div style={{ padding: '4px 10px', fontSize: 11, color: '#ef4444', borderBottom: `1px solid ${borderColor}`, flexShrink: 0 }}>
            {generateError}
          </div>
        )}

        {/* ── Unlinked lane warnings ── */}
        {warnings.length > 0 && (
          <div style={{
            padding: '6px 10px',
            fontSize: 11,
            color: '#b45309',
            background: '#fffbeb',
            borderBottom: `1px solid #fcd34d`,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
          }}>
            <span style={{ fontSize: 13, lineHeight: 1 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>
                {warnings.length === 1
                  ? '1 lane не синхронизирован с системой'
                  : `${warnings.length} lane не синхронизированы с системами`}
              </div>
              <div style={{ opacity: 0.85 }}>
                Добавьте <code style={{ background: '#fef3c7', padding: '0 3px', borderRadius: 2 }}>{'<<КОД_СИСТЕМЫ>>'}</code> после названия lane,
                например: <code style={{ background: '#fef3c7', padding: '0 3px', borderRadius: 2 }}>lane "Backend" {'<<BILLING_SVC>>'} {'{'}</code>
              </div>
            </div>
          </div>
        )}


        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Editor
            key={editorKey}
            defaultValue={editorDefault}
            language="bpmn-dsl"
            theme={monacoTheme}
            onChange={handleDslChange}
            onMount={handleEditorDidMount}
            options={sharedEditorOptions}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const PaneHeader = ({ title, bg, textColor, borderColor, right }) => (
  <div
    style={{
      height: HEADER_H,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 10px',
      borderBottom: `1px solid ${borderColor}`,
      background: bg,
      flexShrink: 0,
    }}
  >
    <span style={{ fontSize: 11, fontWeight: 600, color: textColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {title}
    </span>
    {right}
  </div>
);

const generateButtonStyle = (accentColor) => ({
  background: accentColor,
  border: 'none',
  borderRadius: 4,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  padding: '3px 10px',
  letterSpacing: '0.02em',
});

const closeButtonStyle = (textColor) => ({
  background: 'transparent',
  border: 'none',
  color: textColor,
  cursor: 'pointer',
  fontSize: 14,
  padding: '2px 6px',
  opacity: 0.6,
  lineHeight: 1,
});

export default BpmnCodePanel;
